import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  adminGetEvent, adminUpdateEvent, adminGetVenues, adminGetMcs,
  adminGetPerformersList, adminAddEventPerformer, adminReorderEventPerformers,
  adminUpdateEventPerformer, adminRemoveEventPerformer,
  adminConfirmEvent, adminUnconfirmEvent, adminSaveReconfirmNotes, adminGetEventRecipients,
  adminSendTestConfirmEmail, adminContactPerformer, adminNotifyEventPerformer,
  adminAddEventSlot, adminReorderEventSlots, adminUpdateEventSlot, adminRemoveEventSlot,
  adminCreateAdHocSet, adminDeleteAdHocSet, adminUpdateAdHocSet, adminGetPerformerStageRequirements, adminGetInstrumentTypes,
} from '../services/api';
import Navbar from '../components/Navbar';
import AdminTabBar from '../components/AdminTabBar';

// Slice the first 10 chars (YYYY-MM-DD) directly — avoids new Date() / toISOString()
// which shifts UTC midnight to the previous calendar day in UTC+ timezones.
const toDateStr = (d) => {
  if (!d) return '';
  return String(d).slice(0, 10);
};

const formatDate = (d) => {
  if (!d) return '—';
  return String(d).slice(0, 10).split('-').reverse().join('/');
};

const fmtTime = (t) => {
  if (!t) return '—';
  return t.slice(0, 5);
};

const fmt12h = (t) => {
  if (!t) return '';
  const [h, m] = String(t).slice(0, 5).split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`;
};

function TypeBadge({ eventType }) {
  const map = {
    showcase:          { bg: '#0ea5e9', label: 'Showcase' },
    peoples_choice:    { bg: '#f59e0b', label: "People's Choice" },
    lyricists:         { bg: '#8b5cf6', label: '3 Lyricists' },
    songwriters_round: { bg: '#10b981', label: 'Songwriters in the Round' },
  };
  const t = map[eventType] || { bg: '#6b7280', label: eventType };
  return (
    <span style={{
      background: t.bg, color: '#fff', borderRadius: '4px',
      padding: '2px 10px', fontSize: '13px', fontWeight: 600, display: 'inline-block',
    }}>
      {t.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    planning:  { bg: '#fef9c3', color: '#854d0e', label: 'Planning' },
    confirmed: { bg: '#dcfce7', color: '#166534', label: 'Confirmed' },
    completed: { bg: '#f3f4f6', color: '#6b7280', label: 'Completed' },
  };
  const s = map[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: '9999px',
      padding: '2px 12px', fontSize: '13px', fontWeight: 600, display: 'inline-block',
    }}>
      {s.label}
    </span>
  );
}

// ── Lyricists / Songwriters Matrix Card ──────────────────────────────────────
// isRound=true renders "Set N" headings (no lyric name/writer) for songwriters_round events.
function LyricistMatrixCard({ set, slots, performersList, reordering, onAddSlot, onRemoveSlot, onUpdateSlot, onReorderSlots, isRound }) {
  const setSlots = (slots || [])
    .filter(s => s.lyric_set_id === set.id)
    .sort((a, b) => a.slot_order - b.slot_order);

  return (
    <div className="a-card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
      <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: 'var(--navy)' }}>
        {isRound ? 'Set' : 'Lyric'} {set.set_order}
        {!isRound && set.title && (
          <span style={{ fontWeight: 400, color: 'var(--text)', marginLeft: '0.5rem' }}>
            — {set.title}
            {set.writer && <span style={{ color: 'var(--muted)', fontWeight: 400 }}> · {set.writer}</span>}
          </span>
        )}
      </h3>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.6rem' }}>
        <button className="a-btn a-btn--navy a-btn--sm" onClick={() => onAddSlot(set.id)}>
          + Add Performer
        </button>
      </div>

      {setSlots.length === 0 ? (
        <div style={{ fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
          No performers assigned yet — click "+ Add Performer" to assign performers to this set.
        </div>
      ) : (
        <table className="a-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '36px' }} />
              <th style={{ width: '220px', fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', paddingBottom: '4px' }}>Performers</th>
              <th />
              <th style={{ width: '220px', fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', paddingBottom: '4px' }}>Composer</th>
              <th style={{ width: '80px' }} />
              <th style={{ width: '80px' }} />
            </tr>
          </thead>
          <tbody>
            {setSlots.map((slot, i) => (
              <tr key={slot.id}>
                <td style={{ width: '36px', fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>{i + 1}</td>
                <td style={{ width: '220px' }}>
                  {slot.performer_id ? (
                    <select
                      className="a-select"
                      style={{ margin: 0, padding: '3px 8px', fontSize: '13px', width: '220px' }}
                      value={String(slot.performer_id)}
                      onChange={e => onUpdateSlot(slot.id, { performer_id: parseInt(e.target.value) })}
                    >
                      {performersList.map(p => (
                        <option key={p.id} value={String(p.id)}>{p.stage_name || p.real_name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="a-input"
                      style={{ margin: 0, padding: '3px 8px', fontSize: '13px', width: '220px' }}
                      defaultValue={slot.performer_name || ''}
                      onBlur={e => { if (e.target.value.trim()) onUpdateSlot(slot.id, { performer_name: e.target.value.trim() }); }}
                    />
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      {slot.performer_id && (slot.performer_email || slot.email) && (
                        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{slot.performer_email || slot.email}</span>
                      )}
                      {slot.performer_id && slot.phone && (
                        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{slot.phone}</span>
                      )}
                      {!slot.performer_id && slot.performer_name && (
                        <span style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                          {slot.performer_email || 'free text'}
                        </span>
                      )}
                    </div>
                    {(() => {
                      const instrSummary = (slot.instruments || [])
                        .filter(i => i.instrument)
                        .map(i => `${i.instrument}${i.connection ? ` (${i.connection})` : ''}`)
                        .join(', ');
                      const reqLine = [
                        (slot.vocal_mics > 0) ? `Vocal mics: ${slot.vocal_mics}` : null,
                        instrSummary || null,
                      ].filter(Boolean).join(' · ');
                      return reqLine ? (
                        <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{reqLine}</span>
                      ) : null;
                    })()}
                  </div>
                </td>
                <td style={{ width: '220px' }}>
                  <input
                    className="a-input"
                    list="composers-list"
                    style={{ margin: 0, padding: '3px 8px', fontSize: '13px', width: '200px' }}
                    defaultValue={slot.composer || ''}
                    onBlur={e => onUpdateSlot(slot.id, { composer: e.target.value.trim() })}
                  />
                </td>
                <td style={{ width: '80px' }}>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      className="a-btn a-btn--ghost a-btn--sm"
                      disabled={i === 0 || reordering}
                      onClick={() => onReorderSlots(setSlots[i - 1].id, slot.id)}
                      style={{ padding: '2px 7px' }}
                    >↑</button>
                    <button
                      className="a-btn a-btn--ghost a-btn--sm"
                      disabled={i === setSlots.length - 1 || reordering}
                      onClick={() => onReorderSlots(slot.id, setSlots[i + 1].id)}
                      style={{ padding: '2px 7px' }}
                    >↓</button>
                  </div>
                </td>
                <td style={{ width: '80px' }}>
                  <button
                    className="a-btn a-btn--ghost a-btn--sm"
                    style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                    onClick={() => onRemoveSlot(slot.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <datalist id="composers-list">
        {performersList.map(p => (
          <option key={p.id} value={p.stage_name || p.real_name} />
        ))}
      </datalist>
    </div>
  );
}

// ── Add Performer to Set Modal ─────────────────────────────────────────────────
function AddPerformerToSetModal({ defaultSetId, lyricSets, performersList, instrumentTypes, onAdd, onClose, saving, error, isRound }) {
  const [performerName, setPerformerName]     = useState('');
  const [matchedId, setMatchedId]             = useState(null);
  const [performerEmail, setPerformerEmail]   = useState('');
  const [lyricSetId, setLyricSetId]           = useState(String(defaultSetId));
  const [vocalMics, setVocalMics]             = useState(0);
  const [instruments, setInstruments]         = useState([]);
  const [autoPopulating, setAutoPopulating]   = useState(false);

  const defaultSet = lyricSets.find(s => s.id === defaultSetId);
  const setN = defaultSet ? defaultSet.set_order : '?';

  const addInstrumentRow    = () => setInstruments(prev => [...prev, { instrument: '', connection: 'DI' }]);
  const removeInstrumentRow = (i) => setInstruments(prev => prev.filter((_, idx) => idx !== i));
  const updateInstrumentRow = (i, field, value) =>
    setInstruments(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const handleNameChange = async (e) => {
    const val = e.target.value;
    setPerformerName(val);
    const match = performersList.find(p =>
      (p.stage_name && p.stage_name.toLowerCase() === val.toLowerCase()) ||
      (p.real_name  && p.real_name.toLowerCase()  === val.toLowerCase())
    );
    const newId = match ? match.id : null;
    setMatchedId(newId);
    if (newId) {
      setPerformerEmail(match.email || '');
      setAutoPopulating(true);
      try {
        const res = await adminGetPerformerStageRequirements(newId);
        setVocalMics(res.data.vocal_mics || 0);
        setInstruments(res.data.instruments || []);
      } catch {}
      setAutoPopulating(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!performerName.trim() || !lyricSetId) return;
    onAdd({ performerId: matchedId, performerName: performerName.trim(), performerEmail: performerEmail.trim(), lyricSetId: parseInt(lyricSetId), vocalMics, instruments });
  };

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '480px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Add Performer to {isRound ? 'Set' : 'Lyric'} {setN}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div className="a-field">
            <label className="a-label">Performer Name *</label>
            <input
              className="a-input"
              type="text"
              list="add-slot-performers-datalist"
              value={performerName}
              onChange={handleNameChange}
              placeholder="Type or select a performer…"
              autoComplete="off"
              required
            />
            <datalist id="add-slot-performers-datalist">
              {performersList.map(p => (
                <option key={p.id} value={p.stage_name || p.real_name} />
              ))}
            </datalist>
            {autoPopulating && <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Loading stage requirements…</div>}
            {matchedId && !autoPopulating && <div style={{ fontSize: '12px', color: '#15803d', marginTop: '4px' }}>Matched to registered performer</div>}
          </div>
          <div className="a-field">
            <label className="a-label">Email (optional)</label>
            <input
              className="a-input"
              type="email"
              value={performerEmail}
              onChange={e => setPerformerEmail(e.target.value)}
              placeholder="Confirmation email for this performer"
            />
          </div>
          <div className="a-field">
            <label className="a-label">Select {isRound ? 'Set' : 'Lyric'} *</label>
            <select className="a-select" value={lyricSetId} onChange={e => setLyricSetId(e.target.value)} required>
              {lyricSets.map(s => (
                <option key={s.id} value={String(s.id)}>{isRound ? 'Set' : 'Lyric'} {s.set_order}</option>
              ))}
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">Vocal Mics</label>
            <select className="a-select" value={vocalMics} onChange={e => setVocalMics(parseInt(e.target.value))}>
              {[0,1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="a-field" style={{ marginBottom: 0 }}>
            <label className="a-label">Instruments on Stage</label>
            {instruments.map((instr, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <select
                  className="a-select"
                  value={instr.instrument}
                  onChange={e => updateInstrumentRow(i, 'instrument', e.target.value)}
                  style={{ flex: 1, margin: 0 }}
                >
                  <option value="">Select instrument…</option>
                  {instrumentTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
                <select
                  className="a-select"
                  value={instr.connection}
                  onChange={e => updateInstrumentRow(i, 'connection', e.target.value)}
                  style={{ width: '130px', margin: 0 }}
                >
                  <option value="DI">DI</option>
                  <option value="Amplified">Amplified</option>
                  <option value="Acoustic">Acoustic</option>
                  <option value="Other">Other</option>
                </select>
                <button type="button" onClick={() => removeInstrumentRow(i)}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '1.2rem', cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
            <button type="button" className="a-btn a-btn--ghost a-btn--sm" onClick={addInstrumentRow}>
              + Add Instrument
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving || !performerName.trim() || !lyricSetId}>
              {saving ? 'Adding…' : 'Add Performer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Assign MC Modal ──────────────────────────────────────────────────────────
function AssignMCModal({ event, mcs, onSave, onClose, saving, error }) {
  const [mcId, setMcId] = useState(event.mc_id ? String(event.mc_id) : '');

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '420px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Assign MC to {event.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(mcId ? parseInt(mcId) : null); }}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div className="a-field" style={{ marginBottom: 0 }}>
            <label className="a-label">MC</label>
            <select className="a-select" value={mcId} onChange={e => setMcId(e.target.value)}>
              <option value="">None</option>
              {mcs.map(m => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Event Modal ─────────────────────────────────────────────────────────
function EditEventModal({ event, venues, mcs, onSave, onClose, saving, error }) {
  const [form, setForm] = useState({
    name:                   event.name || '',
    event_type:             event.event_type || '',
    event_date:             toDateStr(event.event_date),
    performer_arrival_time: event.performer_arrival_time ? String(event.performer_arrival_time).slice(0, 5) : '',
    show_start_time:        event.show_start_time        ? String(event.show_start_time).slice(0, 5)        : '',
    venue_id:               event.venue_id ? String(event.venue_id) : '',
    mc_id:                  event.mc_id ? String(event.mc_id) : '',
    notes:                  event.notes || '',
    lyricists_count:        event.lyricists_count ?? 3,
    lyrics_sets_count:      event.lyrics_sets_count ?? 3,
    status:                 event.status || 'planning',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '520px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Edit Event</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form); }}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div className="a-field">
            <label className="a-label">Event Name *</label>
            <input className="a-input" type="text" value={form.name} onChange={e => set('name', e.target.value)} required />
          </div>
          <div className="a-field">
            <label className="a-label">Event Type *</label>
            <select className="a-select" value={form.event_type} onChange={e => set('event_type', e.target.value)} required>
              <option value="" disabled>Select a type…</option>
              <option value="showcase">SCALA Showcase</option>
              <option value="peoples_choice">SCALA People's Choice</option>
              <option value="lyricists">3 Lyricists Walk Into a Bar</option>
              <option value="songwriters_round">Songwriters in the Round</option>
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">Event Date *</label>
            <input className="a-input" type="date" value={form.event_date} onChange={e => set('event_date', e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Performer Arrival Time</label>
              <input className="a-input" type="time" value={form.performer_arrival_time} onChange={e => set('performer_arrival_time', e.target.value)} />
            </div>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Show Start Time</label>
              <input className="a-input" type="time" value={form.show_start_time} onChange={e => set('show_start_time', e.target.value)} />
            </div>
          </div>
          <div className="a-field">
            <label className="a-label">Venue</label>
            <select className="a-select" value={form.venue_id} onChange={e => set('venue_id', e.target.value)}>
              <option value="">Select a venue...</option>
              {venues.map(v => <option key={v.id} value={String(v.id)}>{v.name}</option>)}
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">MC</label>
            <select className="a-select" value={form.mc_id} onChange={e => set('mc_id', e.target.value)}>
              <option value="">None</option>
              {mcs.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">Notes</label>
            <textarea className="a-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ resize: 'vertical' }} />
          </div>
          {form.event_type === 'lyricists' && (
            <>
              <div className="a-field">
                <label className="a-label">Number of Lyricists</label>
                <input className="a-input" type="number" min={2} max={10} value={form.lyricists_count} onChange={e => set('lyricists_count', parseInt(e.target.value) || 3)} />
              </div>
              <div className="a-field">
                <label className="a-label">Number of Lyrics</label>
                <input className="a-input" type="number" min={1} max={10} value={form.lyrics_sets_count} onChange={e => set('lyrics_sets_count', parseInt(e.target.value) || 3)} />
              </div>
            </>
          )}
          {form.event_type === 'songwriters_round' && (
            <div className="a-field">
              <label className="a-label">Number of Sets</label>
              <input className="a-input" type="number" min={1} max={10} value={form.lyrics_sets_count} onChange={e => set('lyrics_sets_count', parseInt(e.target.value) || 3)} />
            </div>
          )}
          <div className="a-field">
            <label className="a-label">Status</label>
            <select className="a-select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="planning">Planning</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving}>
              {saving ? 'Saving…' : 'Save Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Performer Modal ──────────────────────────────────────────────────────
function AddPerformerModal({ performersList, instrumentTypes, onAdd, onClose, saving, error }) {
  const [performerName, setPerformerName]   = useState('');
  const [matchedId, setMatchedId]           = useState(null);
  const [performerEmail, setPerformerEmail] = useState('');
  const [vocalMics, setVocalMics]           = useState(0);
  const [instruments, setInstruments]       = useState([]);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const fetchToken = useRef(null);

  const addInstrumentRow    = () => setInstruments(prev => [...prev, { instrument: '', connection: 'DI' }]);
  const removeInstrumentRow = (i) => setInstruments(prev => prev.filter((_, idx) => idx !== i));
  const updateInstrumentRow = (i, field, value) =>
    setInstruments(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const handleNameChange = async (e) => {
    const val = e.target.value;
    setPerformerName(val);

    // Every keystroke immediately resets matched state so the field is always editable.
    // A stale in-flight fetch is cancelled via the token before we start a new one.
    fetchToken.current = null;
    setMatchedId(null);
    setPerformerEmail('');
    setVocalMics(0);
    setInstruments([]);
    setAutoPopulating(false);

    const match = performersList.find(p =>
      (p.stage_name && p.stage_name.toLowerCase() === val.toLowerCase()) ||
      (p.real_name  && p.real_name.toLowerCase()  === val.toLowerCase())
    );
    if (!match) return;

    setMatchedId(match.id);
    setPerformerEmail(match.email || '');
    setAutoPopulating(true);
    const token = {};
    fetchToken.current = token;
    try {
      const res = await adminGetPerformerStageRequirements(match.id);
      if (fetchToken.current !== token) return; // superseded by a later keystroke
      setVocalMics(res.data.vocal_mics || 0);
      setInstruments(res.data.instruments || []);
    } catch {}
    if (fetchToken.current === token) setAutoPopulating(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!performerName.trim()) return;
    onAdd({ performerId: matchedId, performerName: performerName.trim(), performerEmail: performerEmail.trim(), vocalMics, instruments });
  };

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '480px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Add Performer</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div className="a-field">
            <label className="a-label">Performer Name *</label>
            <input
              className="a-input"
              type="text"
              list="add-perf-datalist"
              value={performerName}
              onChange={handleNameChange}
              placeholder="Type or select a performer…"
              autoComplete="off"
              required
            />
            <datalist id="add-perf-datalist">
              {performersList.map(p => (
                <option key={p.id} value={p.stage_name || p.real_name} />
              ))}
            </datalist>
            {autoPopulating && <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>Loading stage requirements…</div>}
            {matchedId && !autoPopulating && <div style={{ fontSize: '12px', color: '#15803d', marginTop: '4px' }}>Matched to registered performer</div>}
          </div>
          <div className="a-field">
            <label className="a-label">Email (optional)</label>
            <input
              className="a-input"
              type="email"
              value={performerEmail}
              onChange={e => setPerformerEmail(e.target.value)}
              placeholder="Confirmation email for this performer"
            />
          </div>
          <div className="a-field">
            <label className="a-label">Vocal Mics</label>
            <select className="a-select" value={vocalMics} onChange={e => setVocalMics(parseInt(e.target.value))}>
              {[0,1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="a-field" style={{ marginBottom: 0 }}>
            <label className="a-label">Instruments on Stage</label>
            {instruments.map((instr, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <select
                  className="a-select"
                  value={instr.instrument}
                  onChange={e => updateInstrumentRow(i, 'instrument', e.target.value)}
                  style={{ flex: 1, margin: 0 }}
                >
                  <option value="">Select instrument…</option>
                  {instrumentTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
                <select
                  className="a-select"
                  value={instr.connection}
                  onChange={e => updateInstrumentRow(i, 'connection', e.target.value)}
                  style={{ width: '130px', margin: 0 }}
                >
                  <option value="DI">DI</option>
                  <option value="Amplified">Amplified</option>
                  <option value="Acoustic">Acoustic</option>
                  <option value="Other">Other</option>
                </select>
                <button type="button" onClick={() => removeInstrumentRow(i)}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '1.2rem', cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>×</button>
              </div>
            ))}
            <button type="button" className="a-btn a-btn--ghost a-btn--sm" onClick={addInstrumentRow}>
              + Add Instrument
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving || !performerName.trim()}>
              {saving ? 'Adding…' : 'Add Performer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Re-send one performer's booking confirmation, as FOOM's per-song "Notify
 * performer" does. Confirming the event emails everybody, so without this a
 * single changed spot time means mailing the whole bill.
 *
 * Asks first: it sends a real email to a real person, and the button sits among
 * reorder arrows that are easy to mis-click.
 */
function RowNotifyControl({ perf, eventId }) {
  const [busy, setBusy] = useState(false);
  const email = perf.performer_email || perf.email || '';
  const name  = perf.stage_name || perf.real_name || perf.performer_name || 'this performer';

  if (!email) return null;

  const notify = async () => {
    if (!window.confirm(
      `Re-send the booking confirmation to ${name} (${email})?\n\n` +
      'It goes to them alone, with the current times and any additional notes for this event.'
    )) return;
    setBusy(true);
    try {
      const res = await adminNotifyEventPerformer(eventId, perf.id);
      alert(`Sent to ${res.data.email}.${res.data.revision ? ' (Worded as a revision.)' : ''}`);
    } catch (err) {
      alert(err.response?.data?.error || 'The email did not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      className="a-btn a-btn--sm"
      // The stronger blue of the Notify buttons in FOOM: this re-sends the real
      // confirmation, so it should not read as the same weight as a free-text note.
      style={{ background: '#0ea5e9', color: '#fff', border: '1px solid #0284c7' }}
      onClick={notify}
      disabled={busy}
      title={`Re-send the booking confirmation to ${name}`}
    >
      {busy ? 'Sending…' : 'Notify'}
    </button>
  );
}

/**
 * Quick email to one performer from the running order, mirroring the per-song
 * Notify/Email buttons in FOOM's heat panel. The common case on an event page is
 * telling one person something — a changed spot time, a parking note — without
 * re-sending confirmations to everybody.
 *
 * Only shown for performers with an account: the endpoint keys off performer_id,
 * so free-text slots have nobody to email.
 */
function RowEmailControl({ perf, eventName }) {
  const [open,    setOpen]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [subject, setSubject] = useState('');
  const [bodyTxt, setBodyTxt] = useState('');

  const name  = perf.stage_name || perf.real_name || perf.performer_name || 'performer';
  const email = perf.performer_email || perf.email || '';
  const first = String(perf.real_name || perf.stage_name || '').trim().split(/\s+/)[0] || 'there';

  if (!perf.performer_id || !email) return null;

  const openDialog = () => {
    setError(''); setSent(false);
    setSubject(eventName ? `${eventName}` : 'SCALA Performer Registry');
    setBodyTxt(`Hi ${first},\n\n\n\n— SCALA`);
    setOpen(true);
  };

  const send = async () => {
    setBusy(true); setError('');
    try {
      await adminContactPerformer(perf.performer_id, { subject, body: bodyTxt });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'The email did not send.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        className="a-btn a-btn--sm"
        style={{ background: '#bae6fd', color: '#075985', border: '1px solid #7dd3fc' }}
        onClick={openDialog}
        title={`Email ${name}`}
      >
        Email
      </button>

      {open && (
        <div className="a-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) setOpen(false); }}>
          <div className="a-modal" style={{ maxWidth: '600px', width: '100%' }}>
            <h2 className="a-modal__title">Email {name}</h2>

            {sent ? (
              <>
                <p style={{ margin: '0 0 1.25rem' }}>Sent to {email}.</p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="a-btn a-btn--navy" onClick={() => setOpen(false)}>Close</button>
                </div>
              </>
            ) : (
              <>
                {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
                <div style={{
                  background: 'var(--surface, #f8f7f4)', border: '1px solid var(--border, #e5e7eb)',
                  borderRadius: '8px', padding: '0.5rem 0.9rem', marginBottom: '1rem', fontSize: '0.83rem',
                }}>
                  To: <strong>{name}</strong> &lt;{email}&gt;
                </div>
                <div className="a-field">
                  <label className="a-label">Subject</label>
                  <input className="a-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div className="a-field" style={{ marginBottom: '0.4rem' }}>
                  <label className="a-label">Message</label>
                  <textarea
                    className="a-input" rows={9} value={bodyTxt}
                    onChange={(e) => setBodyTxt(e.target.value)}
                    style={{ fontSize: '0.85rem', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                  />
                </div>
                <p style={{ fontSize: '0.75rem', color: 'var(--muted)', margin: '0 0 1rem' }}>
                  Goes to this performer only, and is logged against them.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button className="a-btn a-btn--ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
                  <button
                    className="a-btn a-btn--navy"
                    onClick={send}
                    disabled={busy || !subject.trim() || !bodyTxt.trim()}
                  >
                    {busy ? 'Sending…' : 'Send Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ── Edit Slot Times Modal ────────────────────────────────────────────────────
function EditSlotModal({ slot, onSave, onClose, saving, error }) {
  const [arrival, setArrival] = useState(slot.arrival_time ? slot.arrival_time.slice(0, 5) : '');
  const [spot, setSpot]       = useState(slot.spot_time   ? slot.spot_time.slice(0, 5)   : '');

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '360px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
            Times — {slot.stage_name || slot.real_name}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave({ arrival_time: arrival || null, spot_time: spot || null }); }}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Arrival</label>
              <input className="a-input" type="time" value={arrival} onChange={e => setArrival(e.target.value)} />
            </div>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Spot</label>
              <input className="a-input" type="time" value={spot} onChange={e => setSpot(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving}>
              {saving ? 'Saving…' : 'Save Times'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add Ad Hoc Set Modal ──────────────────────────────────────────────────────
function AddAdHocSetModal({ insertAfterLyricSetId, performersList, instrumentTypes, onSave, onClose, saving, error, initialValues }) {
  const [performerName, setPerformerName]   = useState(initialValues?.performerName || '');
  const [startTime, setStartTime]           = useState(initialValues?.startTime || '');
  const [setLength, setSetLength]           = useState(initialValues?.setLength ?? 15);
  const [vocalMics, setVocalMics]           = useState(initialValues?.vocalMics ?? 0);
  const [instruments, setInstruments]       = useState(initialValues?.instruments || []);
  const [autoPopulating, setAutoPopulating] = useState(false);

  const handlePerformerNameChange = async (e) => {
    const value = e.target.value;
    setPerformerName(value);
    const match = performersList.find(p =>
      (p.stage_name && p.stage_name.toLowerCase() === value.toLowerCase()) ||
      (p.real_name  && p.real_name.toLowerCase()  === value.toLowerCase())
    );
    if (match) {
      setAutoPopulating(true);
      try {
        const res = await adminGetPerformerStageRequirements(match.id);
        setVocalMics(res.data.vocal_mics || 0);
        setInstruments(res.data.instruments || []);
      } catch {}
      setAutoPopulating(false);
    }
  };

  const addInstrumentRow    = () => setInstruments(prev => [...prev, { instrument: '', connection: 'DI' }]);
  const removeInstrumentRow = (i) => setInstruments(prev => prev.filter((_, idx) => idx !== i));
  const updateInstrumentRow = (i, field, value) =>
    setInstruments(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!performerName.trim()) return;
    onSave({ insertAfterLyricSetId, performerName: performerName.trim(), startTime, setLength, vocalMics, instruments });
  };

  return (
    <div className="a-modal-backdrop">
      <div className="a-modal" style={{ maxWidth: '480px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>{initialValues ? 'Edit Additional Set' : 'Add Additional Set'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--muted)' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="a-error" style={{ marginBottom: '0.75rem' }}>{error}</div>}
          <div className="a-field">
            <label className="a-label">Performer Name *</label>
            <input
              className="a-input"
              type="text"
              list="adhoc-performers-datalist"
              value={performerName}
              onChange={handlePerformerNameChange}
              placeholder="Type or select a performer…"
              autoComplete="off"
              required
            />
            <datalist id="adhoc-performers-datalist">
              {performersList.map(p => (
                <option key={p.id} value={p.stage_name || p.real_name} />
              ))}
            </datalist>
            {autoPopulating && (
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                Loading stage requirements…
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Start Time</label>
              <input className="a-input" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
            </div>
            <div className="a-field" style={{ margin: 0 }}>
              <label className="a-label">Set Length</label>
              <select className="a-select" value={setLength} onChange={e => setSetLength(parseInt(e.target.value))}>
                {[5,10,15,20,25,30,35,40,45,50,55,60].map(n => (
                  <option key={n} value={n}>{n} min</option>
                ))}
              </select>
            </div>
          </div>
          <div className="a-field" style={{ marginTop: '0.75rem' }}>
            <label className="a-label">Vocal Mics</label>
            <select className="a-select" value={vocalMics} onChange={e => setVocalMics(parseInt(e.target.value))}>
              {[0,1,2,3,4,5,6,7,8].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="a-field">
            <label className="a-label">Instruments</label>
            {instruments.map((instr, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <select
                  className="a-select"
                  value={instr.instrument}
                  onChange={e => updateInstrumentRow(i, 'instrument', e.target.value)}
                  style={{ flex: 1, margin: 0 }}
                >
                  <option value="">Select instrument…</option>
                  {instrumentTypes.map(t => (
                    <option key={t.id} value={t.name}>{t.name}</option>
                  ))}
                </select>
                <select
                  className="a-select"
                  value={instr.connection}
                  onChange={e => updateInstrumentRow(i, 'connection', e.target.value)}
                  style={{ width: '130px', margin: 0 }}
                >
                  <option value="DI">DI</option>
                  <option value="Amplified">Amplified</option>
                  <option value="Acoustic">Acoustic</option>
                  <option value="Other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeInstrumentRow(i)}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '1.2rem', cursor: 'pointer', padding: '0 4px', lineHeight: 1, flexShrink: 0 }}
                >×</button>
              </div>
            ))}
            <button type="button" className="a-btn a-btn--ghost a-btn--sm" onClick={addInstrumentRow}>
              + Add Instrument
            </button>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
            <button type="button" className="a-btn a-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="a-btn a-btn--navy" disabled={saving || !performerName.trim()}>
              {saving ? 'Saving…' : initialValues ? 'Save Changes' : 'Add Set'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Ad Hoc Set Card ───────────────────────────────────────────────────────────
function AdHocSetCard({ set, onRemove, onEdit }) {
  const instrSummary = (set.instruments || [])
    .filter(i => i.instrument)
    .map(i => `${i.instrument}${i.connection ? ` (${i.connection})` : ''}`)
    .join(', ');

  const reqLine = [
    set.vocalMics > 0 ? `Vocal mics: ${set.vocalMics}` : null,
    instrSummary || null,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{
      marginBottom: '1rem', padding: '1.25rem',
      background: '#f0f9ff', borderRadius: '8px',
      border: '1px solid #bae6fd', borderLeft: '4px solid #0ea5e9',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Additional set</span>
            <span style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>not included in voting</span>
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text)', fontWeight: 500 }}>{set.performerName}</div>
          <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '0.2rem' }}>
            {set.startTime && <span>{set.startTime} · </span>}
            {set.setLength} min
          </div>
          {reqLine && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '0.25rem' }}>
              {reqLine}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="a-btn a-btn--ghost a-btn--sm"
            style={{ flexShrink: 0 }}
            onClick={() => onEdit(set)}
          >
            Edit
          </button>
          <button
            className="a-btn a-btn--ghost a-btn--sm"
            style={{ color: 'var(--red)', borderColor: 'var(--red)', flexShrink: 0 }}
            onClick={() => onRemove(set.id)}
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function AdminEventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent]           = useState(null);
  const [performers, setPerformers] = useState([]);
  const [lyricSets, setLyricSets]   = useState([]);
  const [slots, setSlots]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const [venues, setVenues]                 = useState([]);
  const [mcs, setMcs]                       = useState([]);
  const [performersList, setPerformersList] = useState([]);

  const [showEdit, setShowEdit]     = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');

  const [showAddPerformer, setShowAddPerformer] = useState(false);
  const [addSaving, setAddSaving]               = useState(false);
  const [addError, setAddError]                 = useState('');

  const [editSlot, setEditSlot]     = useState(null);
  const [slotSaving, setSlotSaving] = useState(false);
  const [slotError, setSlotError]   = useState('');

  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [removing, setRemoving]           = useState(false);

  const [reordering, setReordering] = useState(false);

  const [showAddSlot, setShowAddSlot]   = useState(null); // { setId: number }
  const [addSlotSaving, setAddSlotSaving] = useState(false);
  const [addSlotError, setAddSlotError]   = useState('');

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirming, setConfirming]             = useState(false);
  const [confirmError, setConfirmError]         = useState('');
  const [confirmed, setConfirmed]               = useState(false);

  const [reconfirmNotesInput, setReconfirmNotesInput] = useState('');
  const [savingReconfirmNotes, setSavingReconfirmNotes] = useState(false);
  const [reconfirmNotesSaved, setReconfirmNotesSaved] = useState(false);
  const [recipients, setRecipients] = useState(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const [showAssignMC, setShowAssignMC]       = useState(false);
  const [assignMCSaving, setAssignMCSaving]   = useState(false);
  const [assignMCError, setAssignMCError]     = useState('');

  const [adHocSets, setAdHocSets]           = useState([]);
  const [showAdHocModal, setShowAdHocModal] = useState(null);
  const [editAdHocSet, setEditAdHocSet]     = useState(null);
  const [adHocSaving, setAdHocSaving]       = useState(false);
  const [adHocError, setAdHocError]         = useState('');
  const [instrumentTypes, setInstrumentTypes] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    adminGetEvent(id)
      .then(r => {
        const { performers: perfs = [], lyric_sets: sets = [], slots: slts = [], adhoc_sets: adhocs = [], ...ev } = r.data;
        setEvent(ev);
        setReconfirmNotesInput(ev.reconfirm_notes || '');
        setPerformers(perfs);
        setLyricSets(sets);
        setSlots(slts);
        setAdHocSets(adhocs.map(a => ({
          id:                      a.id,
          insertAfterLyricSetId:   a.insert_after_lyric_set_id === null ? 'start' : a.insert_after_lyric_set_id,
          performerName:           a.performer_name,
          startTime:               a.start_time ? String(a.start_time).slice(0, 5) : '',
          setLength:               a.set_length,
          vocalMics:               a.vocal_mics || 0,
          instruments:             a.instruments || [],
        })));
      })
      .catch(() => setError('Failed to load event.'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    adminGetVenues().then(r => setVenues(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetMcs().then(r => setMcs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetPerformersList().then(r => setPerformersList(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    adminGetInstrumentTypes().then(r => setInstrumentTypes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setRecipientsLoading(true);
    adminGetEventRecipients(id)
      .then(r => setRecipients(r.data))
      .catch(() => setRecipients(null))
      .finally(() => setRecipientsLoading(false));
  }, [id, performers]); // re-fetch when the running order changes, not just on first load

  // ── Edit event ────────────────────────────────────────────────────────────
  const handleEditSave = async (form) => {
    setEditSaving(true);
    setEditError('');
    try {
      await adminUpdateEvent(id, {
        name:                   form.name,
        event_type:             form.event_type,
        event_date:             form.event_date,
        performer_arrival_time: form.performer_arrival_time || null,
        show_start_time:        form.show_start_time        || null,
        venue_id:               form.venue_id ? parseInt(form.venue_id) : null,
        mc_id:                  form.mc_id ? parseInt(form.mc_id) : null,
        status:                 form.status,
        lyricists_count:        parseInt(form.lyricists_count) || 3,
        lyrics_sets_count:      parseInt(form.lyrics_sets_count) || 3,
        notes:                  form.notes || null,
      });
      setShowEdit(false);
      load();
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to save event.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Running order / performer management ──────────────────────────────────
  const handleAddPerformer = async ({ performerId, performerName, performerEmail, vocalMics, instruments }) => {
    setAddSaving(true);
    setAddError('');
    try {
      await adminAddEventPerformer(id, {
        ...(performerId ? { performer_id: performerId } : { performer_name: performerName }),
        performer_email: performerEmail || null,
        vocal_mics:      vocalMics || 0,
        instruments:     instruments || [],
      });
      setShowAddPerformer(false);
      load();
    } catch (err) {
      setAddError(err?.response?.data?.error || 'Failed to add performer.');
    } finally {
      setAddSaving(false);
    }
  };

  const handleReorder = async (indexA, indexB) => {
    if (reordering) return;
    setReordering(true);
    const epA = performers[indexA];
    const epB = performers[indexB];
    setPerformers(prev => {
      const next = [...prev];
      [next[indexA], next[indexB]] = [next[indexB], next[indexA]];
      return next;
    });
    try {
      await adminReorderEventPerformers(id, { id_a: epA.id, id_b: epB.id });
    } catch {
      load();
    } finally {
      setReordering(false);
    }
  };

  const handleEditSlotSave = async (data) => {
    setSlotSaving(true);
    setSlotError('');
    try {
      await adminUpdateEventPerformer(id, editSlot.id, data);
      setEditSlot(null);
      load();
    } catch (err) {
      setSlotError(err?.response?.data?.error || 'Failed to save times.');
    } finally {
      setSlotSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeConfirm) return;
    setRemoving(true);
    try {
      await adminRemoveEventPerformer(id, removeConfirm.id);
      setRemoveConfirm(null);
      load();
    } catch {
      alert('Failed to remove performer.');
      setRemoveConfirm(null);
    } finally {
      setRemoving(false);
    }
  };

  // ── Slot management ──────────────────────────────────────────────────────
  const handleAddSlot = async ({ performerId, performerName, performerEmail, lyricSetId, vocalMics, instruments }) => {
    setAddSlotSaving(true);
    setAddSlotError('');
    try {
      const payload = {
        ...(performerId
          ? { performer_id: performerId }
          : { performer_name: performerName }),
        performer_email: performerEmail || null,
        lyric_set_id: lyricSetId,
        vocal_mics:   vocalMics  ?? 0,
        instruments:  instruments ?? [],
      };
      await adminAddEventSlot(id, payload);
      setShowAddSlot(null);
      load();
    } catch (err) {
      setAddSlotError(err?.response?.data?.error || 'Failed to add slot.');
    } finally {
      setAddSlotSaving(false);
    }
  };

  const handleRemoveSlot = async (slotId) => {
    try {
      await adminRemoveEventSlot(id, slotId);
      setSlots(prev => prev.filter(s => s.id !== slotId));
    } catch {
      alert('Failed to remove slot.');
    }
  };

  const handleUpdateSlot = async (slotId, data) => {
    try {
      const res = await adminUpdateEventSlot(id, slotId, data);
      setSlots(prev => prev.map(s => s.id === slotId ? { ...s, ...res.data } : s));
    } catch {
      alert('Failed to update slot.');
      load();
    }
  };

  const handleReorderSlots = async (idA, idB) => {
    if (reordering) return;
    setReordering(true);
    setSlots(prev => {
      const next = [...prev];
      const iA = next.findIndex(s => s.id === idA);
      const iB = next.findIndex(s => s.id === idB);
      if (iA >= 0 && iB >= 0) {
        const orderA = next[iA].slot_order;
        next[iA] = { ...next[iA], slot_order: next[iB].slot_order };
        next[iB] = { ...next[iB], slot_order: orderA };
      }
      return next;
    });
    try {
      await adminReorderEventSlots(id, { id_a: idA, id_b: idB });
    } catch {
      load();
    } finally {
      setReordering(false);
    }
  };

  // ── Assign MC ────────────────────────────────────────────────────────────
  const handleAssignMC = async (mcId) => {
    setAssignMCSaving(true);
    setAssignMCError('');
    try {
      await adminUpdateEvent(id, {
        name:              event.name,
        event_type:        event.event_type,
        event_date:        event.event_date,
        venue_id:          event.venue_id || null,
        mc_id:             mcId,
        status:            event.status,
        lyricists_count:   event.lyricists_count || 3,
        lyrics_sets_count: event.lyrics_sets_count || 3,
        notes:             event.notes || null,
      });
      setShowAssignMC(false);
      load();
    } catch (err) {
      setAssignMCError(err?.response?.data?.error || 'Failed to assign MC.');
    } finally {
      setAssignMCSaving(false);
    }
  };

  // ── Confirm event ─────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmError('');
    try {
      await adminConfirmEvent(id);
      setEvent(prev => ({ ...prev, status: 'confirmed' }));
      setShowConfirmModal(false);
      setConfirmed(true);
    } catch (err) {
      setConfirmError(err?.response?.data?.error || 'Failed to confirm event.');
    } finally {
      setConfirming(false);
    }
  };

  const handleSaveReconfirmNotes = async () => {
    setSavingReconfirmNotes(true);
    setReconfirmNotesSaved(false);
    try {
      await adminSaveReconfirmNotes(id, reconfirmNotesInput);
      setEvent(prev => ({ ...prev, reconfirm_notes: reconfirmNotesInput }));
      setReconfirmNotesSaved(true);
      setTimeout(() => setReconfirmNotesSaved(false), 2000);
    } catch {
      // Non-critical — leave the typed value in place, admin can retry.
    } finally {
      setSavingReconfirmNotes(false);
    }
  };

  // Sends the confirmation email to the logged-in user alone. Deliberately posts
  // the current reconfirm notes first, so the test reflects what is on screen
  // rather than what was last saved.
  const handleTestConfirmEmail = async () => {
    setTestingEmail(true);
    setConfirmError('');
    try {
      if (reconfirmNotesInput !== (event?.reconfirm_notes || '')) {
        await adminSaveReconfirmNotes(id, reconfirmNotesInput);
      }
      const res = await adminSendTestConfirmEmail(id);
      alert(`Test sent to ${res.data.email}. Nobody else received it.`);
    } catch (err) {
      setConfirmError(err.response?.data?.error || 'The test email did not send.');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleUnconfirm = async () => {
    setConfirming(true);
    setConfirmError('');
    try {
      await adminUnconfirmEvent(id);
      setEvent(prev => ({ ...prev, status: 'planning' }));
      setConfirmed(false);
    } catch (err) {
      setConfirmError(err?.response?.data?.error || 'Failed to revert event.');
    } finally {
      setConfirming(false);
    }
  };

  // ── Ad hoc sets ───────────────────────────────────────────────────────────
  const handleAddAdHocSet = async (formData) => {
    setAdHocSaving(true);
    setAdHocError('');
    try {
      const payload = {
        insert_after_lyric_set_id: formData.insertAfterLyricSetId === 'start' ? null : formData.insertAfterLyricSetId,
        performer_name: formData.performerName,
        start_time:     formData.startTime || null,
        set_length:     formData.setLength,
        vocal_mics:     formData.vocalMics,
        instruments:    formData.instruments,
      };
      const res = await adminCreateAdHocSet(id, payload);
      const a = res.data;
      setAdHocSets(prev => [...prev, {
        id:                    a.id,
        insertAfterLyricSetId: a.insert_after_lyric_set_id === null ? 'start' : a.insert_after_lyric_set_id,
        performerName:         a.performer_name,
        startTime:             a.start_time ? String(a.start_time).slice(0, 5) : '',
        setLength:             a.set_length,
        vocalMics:             a.vocal_mics || 0,
        instruments:           a.instruments || [],
      }]);
      setShowAdHocModal(null);
    } catch (err) {
      setAdHocError(err?.response?.data?.error || 'Failed to add ad hoc set.');
    } finally {
      setAdHocSaving(false);
    }
  };

  const handleRemoveAdHocSet = async (adHocId) => {
    try {
      await adminDeleteAdHocSet(id, adHocId);
      setAdHocSets(prev => prev.filter(s => s.id !== adHocId));
    } catch {
      alert('Failed to remove ad hoc set.');
    }
  };

  const handleEditAdHocSet = async (formData) => {
    setAdHocSaving(true);
    setAdHocError('');
    try {
      const payload = {
        insert_after_lyric_set_id: formData.insertAfterLyricSetId === 'start' ? null : formData.insertAfterLyricSetId,
        performer_name: formData.performerName,
        start_time:     formData.startTime || null,
        set_length:     formData.setLength,
        vocal_mics:     formData.vocalMics,
        instruments:    formData.instruments,
      };
      const res = await adminUpdateAdHocSet(id, editAdHocSet.id, payload);
      const a = res.data;
      setAdHocSets(prev => prev.map(s => s.id === editAdHocSet.id ? {
        id:                    a.id,
        insertAfterLyricSetId: a.insert_after_lyric_set_id === null ? 'start' : a.insert_after_lyric_set_id,
        performerName:         a.performer_name,
        startTime:             a.start_time ? String(a.start_time).slice(0, 5) : '',
        setLength:             a.set_length,
        vocalMics:             a.vocal_mics || 0,
        instruments:           a.instruments || [],
      } : s));
      setEditAdHocSet(null);
    } catch (err) {
      setAdHocError(err?.response?.data?.error || 'Failed to save changes.');
    } finally {
      setAdHocSaving(false);
    }
  };

  // ── Ballot PDF ────────────────────────────────────────────────────────────
  const downloadBallot = () => {
    const token = localStorage.getItem('token');
    fetch(`/api/admin/events/${id}/ballot-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ballot-event-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to download ballot PDF.'));
  };

  const downloadSoundie = () => {
    const token = localStorage.getItem('token');
    fetch(`/api/admin/events/${id}/soundie-pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed');
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `soundie-event-${id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => alert('Failed to download Soundie PDF.'));
  };

  if (loading) return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--muted)' }}>Loading…</div>
    </>
  );

  if (error || !event) return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div className="a-error" style={{ marginBottom: '1rem' }}>{error || 'Event not found.'}</div>
        <Link to="/admin/events" style={{ color: 'var(--blue)', fontSize: '14px' }}>← Back to Events</Link>
      </div>
    </>
  );

  const isLyricists   = event.event_type === 'lyricists';
  const isRound       = event.event_type === 'songwriters_round';
  const isMatrix      = isLyricists || isRound;
  const hasBallot     = event.event_type === 'peoples_choice' || isLyricists;
  const isConfirmed   = event.status === 'confirmed';
  const showEventCard = event.status === 'planning' || isConfirmed;

  return (
    <>
      <Navbar />
      <AdminTabBar />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>

        <button
          onClick={() => navigate('/admin/events')}
          style={{
            background: '#22c55e', color: '#fff', border: 'none',
            borderRadius: '6px', padding: '8px 16px', fontSize: '14px',
            fontWeight: 500, cursor: 'pointer', marginBottom: '16px',
            display: 'inline-block',
          }}
        >
          ← Back to Events
        </button>

        {confirmed && (
          <div style={{
            background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px',
            padding: '0.75rem 1.25rem', marginBottom: '1.25rem', color: '#166534', fontWeight: 500,
          }}>
            Event confirmed — notifications sent to performers, booking managers, and sound engineers.
          </div>
        )}

        {/* Event header */}
        <div className="a-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--navy)', margin: '0 0 0.5rem' }}>
                {event.name}
              </h1>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <TypeBadge eventType={event.event_type} />
                <StatusBadge status={event.status} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              {hasBallot && (
                <button className="a-btn a-btn--ghost a-btn--sm" onClick={downloadBallot}>
                  Ballot PDF
                </button>
              )}
              <button className="a-btn a-btn--ghost a-btn--sm" onClick={downloadSoundie}>
                Soundie PDF
              </button>
              <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => { setShowEdit(true); setEditError(''); }}>
                Edit
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex', gap: '2rem', flexWrap: 'wrap',
            marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb',
          }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Date</div>
              <div style={{ fontSize: '14px', color: 'var(--text)' }}>{formatDate(event.event_date)}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Venue</div>
              <div style={{ fontSize: '14px', color: 'var(--text)' }}>{event.venue_name || '—'}</div>
              {event.venue_address && <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{event.venue_address}</div>}
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>MC</div>
              <div style={{ fontSize: '14px', color: 'var(--text)' }}>{event.mc_name || '—'}</div>
            </div>
            {event.performer_arrival_time && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Performers Arrive</div>
                <div style={{ fontSize: '14px', color: 'var(--text)' }}>{fmt12h(event.performer_arrival_time)}</div>
              </div>
            )}
            {event.show_start_time && (
              <div>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Show Starts</div>
                <div style={{ fontSize: '14px', color: 'var(--text)' }}>{fmt12h(event.show_start_time)}</div>
              </div>
            )}
            {event.notes && (
              <div style={{ flexBasis: '100%' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Notes</div>
                <div style={{ fontSize: '14px', color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{event.notes}</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Lyricists / Songwriters matrix view ────────────────────────── */}
        {isMatrix ? (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                {isRound ? 'Songwriters in the Round' : 'Lyricists Performance Matrix'}
              </h2>
              <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => { setShowAssignMC(true); setAssignMCError(''); }}>
                {event.mc_id ? 'Change MC' : 'Assign MC'}
              </button>
            </div>
            {lyricSets.length === 0 ? (
              <div style={{
                textAlign: 'center', color: 'var(--muted)', padding: '2rem', fontSize: '14px',
                background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb',
              }}>
                No lyrics configured.
              </div>
            ) : (
              <>
                {isLyricists && (
                  <>
                    {adHocSets.filter(a => a.insertAfterLyricSetId === 'start').map(adHoc => (
                      <AdHocSetCard key={adHoc.id} set={adHoc} onRemove={handleRemoveAdHocSet} onEdit={s => { setEditAdHocSet(s); setAdHocError(''); }} />
                    ))}
                    <div style={{ margin: '12px 0' }}>
                      <button
                        style={{
                          background: '#0ea5e9', color: '#fff', border: 'none',
                          borderRadius: '6px', padding: '6px 14px', fontSize: '13px',
                          fontWeight: 500, cursor: 'pointer',
                        }}
                        onClick={() => setShowAdHocModal('start')}
                      >
                        + Add additional set
                      </button>
                    </div>
                  </>
                )}
                {lyricSets.map(set => (
                  <React.Fragment key={set.id}>
                    <LyricistMatrixCard
                      set={set}
                      slots={slots}
                      performersList={performersList}
                      reordering={reordering}
                      isRound={isRound}
                      onAddSlot={(setId) => { setShowAddSlot({ setId }); setAddSlotError(''); }}
                      onRemoveSlot={handleRemoveSlot}
                      onUpdateSlot={handleUpdateSlot}
                      onReorderSlots={handleReorderSlots}
                    />
                    {isLyricists && adHocSets.filter(a => a.insertAfterLyricSetId === set.id).map(adHoc => (
                      <AdHocSetCard key={adHoc.id} set={adHoc} onRemove={handleRemoveAdHocSet} onEdit={s => { setEditAdHocSet(s); setAdHocError(''); }} />
                    ))}
                    {isLyricists && (
                      <div style={{ margin: '12px 0' }}>
                        <button
                          style={{
                            background: '#0ea5e9', color: '#fff', border: 'none',
                            borderRadius: '6px', padding: '6px 14px', fontSize: '13px',
                            fontWeight: 500, cursor: 'pointer',
                          }}
                          onClick={() => setShowAdHocModal(set.id)}
                        >
                          + Add additional set
                        </button>
                      </div>
                    )}
                  </React.Fragment>
                ))}
                {isLyricists && adHocSets.filter(a => a.insertAfterLyricSetId === 'end').map(adHoc => (
                  <AdHocSetCard key={adHoc.id} set={adHoc} onRemove={handleRemoveAdHocSet} onEdit={s => { setEditAdHocSet(s); setAdHocError(''); }} />
                ))}
              </>
            )}
          </div>
        ) : (
          /* ── Showcase / People's Choice: plain running order ──────────── */
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Running Order</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="a-btn a-btn--ghost a-btn--sm" onClick={() => { setShowAssignMC(true); setAssignMCError(''); }}>
                  {event.mc_id ? 'Change MC' : 'Assign MC'}
                </button>
                <button className="a-btn a-btn--navy a-btn--sm" onClick={() => { setShowAddPerformer(true); setAddError(''); }}>
                  Add Performer
                </button>
              </div>
            </div>

            {performers.length === 0 ? (
              <div style={{
                textAlign: 'center', color: 'var(--muted)', padding: '2rem', fontSize: '14px',
                background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb',
              }}>
                No performers added yet. Click "Add Performer" to build the running order.
              </div>
            ) : (
              <div className="a-card" style={{ padding: 0, overflow: 'hidden' }}>
                <table className="a-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                      <th>Performer</th>
                      <th style={{ width: '150px' }}>Set Type</th>
                      <th style={{ width: '100px' }}>Arrival</th>
                      <th style={{ width: '100px' }}>Spot</th>
                      <th style={{ width: '180px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {performers.map((perf, i) => (
                      <tr key={perf.id}>
                        <td style={{ fontWeight: 700, color: 'var(--navy)', textAlign: 'center' }}>{i + 1}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{perf.stage_name || perf.real_name || perf.performer_name}</div>
                          {perf.performance_type && (
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{perf.performance_type}</div>
                          )}
                          {(perf.email || perf.ep_performer_email) && (
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{perf.email || perf.ep_performer_email}</div>
                          )}
                          {perf.phone && (
                            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{perf.phone}</div>
                          )}
                          {((perf.vocal_mics > 0) || (perf.instruments && perf.instruments.length > 0)) && (
                            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                              {perf.vocal_mics > 0 && <span>{perf.vocal_mics} mic{perf.vocal_mics !== 1 ? 's' : ''}</span>}
                              {perf.vocal_mics > 0 && perf.instruments && perf.instruments.length > 0 && <span> · </span>}
                              {perf.instruments && perf.instruments.length > 0 && (
                                <span>{perf.instruments.map(ins => ins.instrument).filter(Boolean).join(', ')}</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          <select
                            className="a-select"
                            style={{ margin: 0, padding: '3px 8px', fontSize: '13px' }}
                            value={perf.set_type || 'Showcase'}
                            onChange={async (e) => {
                              const newType = e.target.value;
                              setPerformers(prev => prev.map(p => p.id === perf.id ? { ...p, set_type: newType } : p));
                              try {
                                await adminUpdateEventPerformer(id, perf.id, { set_type: newType });
                              } catch { load(); }
                            }}
                          >
                            <option value="Showcase">Showcase</option>
                            <option value="Spotlight">Spotlight</option>
                          </select>
                        </td>
                        <td style={{ color: 'var(--muted)', fontSize: '14px' }}>{fmtTime(perf.arrival_time)}</td>
                        <td style={{ color: 'var(--muted)', fontSize: '14px' }}>{fmtTime(perf.spot_time)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.3rem' }}>
                            <button
                              className="a-btn a-btn--ghost a-btn--sm"
                              disabled={i === 0 || reordering}
                              onClick={() => handleReorder(i - 1, i)}
                              style={{ padding: '2px 7px' }}
                            >↑</button>
                            <button
                              className="a-btn a-btn--ghost a-btn--sm"
                              disabled={i === performers.length - 1 || reordering}
                              onClick={() => handleReorder(i, i + 1)}
                              style={{ padding: '2px 7px' }}
                            >↓</button>
                            <RowNotifyControl perf={perf} eventId={id} />
                            <RowEmailControl perf={perf} eventName={event?.name} />
                            <button
                              className="a-btn a-btn--ghost a-btn--sm"
                              onClick={() => { setEditSlot(perf); setSlotError(''); }}
                            >
                              Times
                            </button>
                            <button
                              className="a-btn a-btn--ghost a-btn--sm"
                              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                              onClick={() => setRemoveConfirm(perf)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Confirm / Revise Event ─────────────────────────────────────── */}
        {showEventCard && (
          <div>
            {confirmError && <div className="a-error" style={{ marginBottom: '0.5rem' }}>{confirmError}</div>}
          <div style={{
            background: '#fff', borderRadius: '8px',
            border: `1px solid ${isConfirmed ? '#fde68a' : '#e5e7eb'}`,
            padding: '1.25rem 1.5rem',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: '0.2rem' }}>
                {isConfirmed ? 'This event is confirmed' : 'Ready to re-confirm this event?'}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                {isConfirmed
                  ? 'Revising will revert the status to Planning so you can make changes and re-confirm.'
                  : 'Re-sends confirmation emails to all current performers, booking managers, and sound engineers.'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              {isConfirmed ? (
                <button
                  className="a-btn a-btn--ghost"
                  style={{ borderColor: '#d97706', color: '#d97706' }}
                  onClick={handleUnconfirm}
                  disabled={confirming}
                >
                  {confirming ? 'Reverting…' : 'Revise booked event'}
                </button>
              ) : (
                <>
                  <button
                    className="a-btn"
                    // Light blue, as the comms actions are across both apps: a test
                    // that reaches only you should not look like the send that
                    // reaches everyone.
                    style={{ background: '#bae6fd', color: '#075985', border: '1px solid #7dd3fc' }}
                    onClick={handleTestConfirmEmail}
                    disabled={testingEmail}
                    title="Send this confirmation email to yourself only"
                  >
                    {testingEmail ? 'Sending…' : 'Send Test Email to Me'}
                  </button>
                  <button
                    className="a-btn a-btn--navy"
                    style={{ background: '#16a34a', borderColor: '#16a34a' }}
                    onClick={() => { setShowConfirmModal(true); setConfirmError(''); }}
                  >
                    Confirm/Reconfirm Event
                  </button>
                </>
              )}
            </div>
          </div>

          {!isConfirmed && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', display: 'block', marginBottom: '0.35rem' }}>
                Additional notes for this event{' '}
                <span style={{ fontWeight: 400, color: 'var(--muted)' }}>(included in the reconfirmation email when set)</span>
              </label>
              <textarea
                className="a-input"
                rows={3}
                value={reconfirmNotesInput}
                onChange={e => setReconfirmNotesInput(e.target.value)}
                onBlur={handleSaveReconfirmNotes}
                style={{ resize: 'vertical', width: '100%' }}
                placeholder="e.g. parking has changed, load-in time moved earlier…"
              />
              <div style={{ fontSize: '12px', color: savingReconfirmNotes ? 'var(--muted)' : '#16a34a', marginTop: '0.25rem', minHeight: '1em' }}>
                {savingReconfirmNotes ? 'Saving…' : reconfirmNotesSaved ? 'Saved' : ''}
              </div>

              <div style={{ marginTop: '0.85rem' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.35rem' }}>
                  Recipients
                  {recipients && (
                    ` (${(recipients.performers?.length || 0) + (recipients.booking_managers?.length || 0) + (recipients.sound_engineers?.length || 0)})`
                  )}
                </div>
                {recipientsLoading ? (
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Loading…</div>
                ) : !recipients ? (
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Could not load recipients.</div>
                ) : (
                  <div style={{ fontSize: '13px', color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div>
                      <strong>Performers ({recipients.performers.length})</strong>
                      {recipients.performers.length === 0
                        ? <div style={{ color: 'var(--muted)' }}>None yet</div>
                        : <div>{recipients.performers.map(p => `${p.name} <${p.email}>`).join(', ')}</div>}
                    </div>
                    <div>
                      <strong>Booking Managers ({recipients.booking_managers.length})</strong>
                      {recipients.booking_managers.length === 0
                        ? <div style={{ color: 'var(--muted)' }}>None yet</div>
                        : <div>{recipients.booking_managers.join(', ')}</div>}
                    </div>
                    <div>
                      <strong>Sound Engineers ({recipients.sound_engineers.length})</strong>
                      {recipients.sound_engineers.length === 0
                        ? <div style={{ color: 'var(--muted)' }}>None yet</div>
                        : <div>{recipients.sound_engineers.join(', ')}</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
          </div>
        )}

      </div>

      {/* ── Modals ──────────────────────────────────────────────────────── */}

      {showAssignMC && (
        <AssignMCModal
          event={event}
          mcs={mcs}
          onSave={handleAssignMC}
          onClose={() => setShowAssignMC(false)}
          saving={assignMCSaving}
          error={assignMCError}
        />
      )}

      {showEdit && (
        <EditEventModal
          event={event}
          venues={venues}
          mcs={mcs}
          onSave={handleEditSave}
          onClose={() => setShowEdit(false)}
          saving={editSaving}
          error={editError}
        />
      )}

      {showAddPerformer && (
        <AddPerformerModal
          performersList={performersList}
          instrumentTypes={instrumentTypes}
          onAdd={handleAddPerformer}
          onClose={() => setShowAddPerformer(false)}
          saving={addSaving}
          error={addError}
        />
      )}

      {showAddSlot && (
        <AddPerformerToSetModal
          defaultSetId={showAddSlot.setId}
          lyricSets={lyricSets}
          performersList={performersList}
          instrumentTypes={instrumentTypes}
          isRound={isRound}
          onAdd={handleAddSlot}
          onClose={() => setShowAddSlot(null)}
          saving={addSlotSaving}
          error={addSlotError}
        />
      )}

      {editSlot && (
        <EditSlotModal
          slot={editSlot}
          onSave={handleEditSlotSave}
          onClose={() => setEditSlot(null)}
          saving={slotSaving}
          error={slotError}
        />
      )}

      {removeConfirm && (
        <div className="a-modal-backdrop">
          <div className="a-modal" style={{ maxWidth: '420px' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 700 }}>Remove Performer</h2>
            <p style={{ margin: '0 0 1.25rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              Remove <strong>{removeConfirm.stage_name || removeConfirm.real_name}</strong> from the running order?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setRemoveConfirm(null)} disabled={removing}>Cancel</button>
              <button className="a-btn a-btn--danger" onClick={handleRemove} disabled={removing}>
                {removing ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdHocModal !== null && (
        <AddAdHocSetModal
          insertAfterLyricSetId={showAdHocModal}
          performersList={performersList}
          instrumentTypes={instrumentTypes}
          onSave={handleAddAdHocSet}
          onClose={() => { setShowAdHocModal(null); setAdHocError(''); }}
          saving={adHocSaving}
          error={adHocError}
        />
      )}

      {editAdHocSet !== null && (
        <AddAdHocSetModal
          insertAfterLyricSetId={editAdHocSet.insertAfterLyricSetId}
          performersList={performersList}
          instrumentTypes={instrumentTypes}
          initialValues={editAdHocSet}
          onSave={handleEditAdHocSet}
          onClose={() => { setEditAdHocSet(null); setAdHocError(''); }}
          saving={adHocSaving}
          error={adHocError}
        />
      )}

      {showConfirmModal && (
        <div className="a-modal-backdrop">
          <div className="a-modal" style={{ maxWidth: '480px' }}>
            <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 700 }}>Confirm Event</h2>
            <p style={{ margin: '0 0 0.75rem', color: 'var(--text)', fontSize: '0.9rem' }}>
              This will send confirmation emails to all booked performers, booking managers, and sound engineers.
            </p>
            {confirmError && <div className="a-error" style={{ marginBottom: '1rem' }}>{confirmError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button className="a-btn a-btn--ghost" onClick={() => setShowConfirmModal(false)} disabled={confirming}>Cancel</button>
              <button
                className="a-btn a-btn--navy"
                style={{ background: '#16a34a', borderColor: '#16a34a' }}
                onClick={handleConfirm}
                disabled={confirming}
              >
                {confirming ? 'Sending…' : 'Confirm & Send Emails'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
